import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import BrokenImageRoundedIcon from "@mui/icons-material/BrokenImageRounded";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import InsertPhotoRoundedIcon from "@mui/icons-material/InsertPhotoRounded";
import {
  fileLabelFromAsset,
  resolveLandingAssetUrl,
} from "../../../utils/landingAssets";
import {
  LANDING_IMAGE_MAX_BYTES,
  acceptedImageFormatsLabel,
  describeImageUploadError,
  formatImageSize,
  uploadProgressPercent,
  validateImageFile,
} from "../../../utils/imageUpload";

export default function EditableImage({
  value,
  label,
  helperText = "",
  height = 180,
  disabled = false,
  accept = "image/svg+xml,image/png,image/jpeg,image/webp,.svg,.png,.jpg,.jpeg,.webp",
  maxBytes = LANDING_IMAGE_MAX_BYTES,
  onUpload,
  onRemove,
}) {
  const inputRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);
  const [progress, setProgress] = React.useState(null);
  const [error, setError] = React.useState("");
  const [previewBroken, setPreviewBroken] = React.useState(false);
  const rawValue = String(value || "").trim();
  const hasReference = Boolean(rawValue);
  const imageUrl = resolveLandingAssetUrl(rawValue);
  const invalidReference = hasReference && !imageUrl;
  const unavailableReference = invalidReference || previewBroken;
  const formatLabel = acceptedImageFormatsLabel(accept);

  React.useEffect(() => {
    setPreviewBroken(false);
  }, [rawValue, imageUrl]);

  const upload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    try {
      validateImageFile(file, {
        accept,
        maxBytes,
        fieldLabel: label || "la imagen",
      });
      setBusy(true);
      setProgress(null);
      setError("");
      setPreviewBroken(false);
      await onUpload?.(file, {
        onUploadProgress: (progressEvent) => {
          const percent = uploadProgressPercent(progressEvent);
          if (percent !== null) setProgress(percent);
        },
      });
      setProgress(100);
    } catch (err) {
      setError(
        describeImageUploadError(err, {
          action: `subir ${String(label || "la imagen").toLowerCase()}`,
          maxBytes,
        }),
      );
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const remove = async () => {
    try {
      setBusy(true);
      setProgress(null);
      setError("");
      await onRemove?.();
      setPreviewBroken(false);
    } catch (err) {
      setError(err?.message || "No se pudo eliminar la imagen");
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <Stack spacing={1}>
      <Typography sx={{ fontWeight: 900, color: "#19242F" }}>{label}</Typography>
      <Box
        sx={{
          position: "relative",
          minHeight: height,
          borderRadius: 2.5,
          overflow: "hidden",
          display: "grid",
          placeItems: "center",
          bgcolor: "rgba(25,36,47,.045)",
          border: unavailableReference
            ? "1px dashed rgba(211,47,47,.42)"
            : "1px dashed rgba(25,36,47,.18)",
        }}
      >
        {imageUrl && !previewBroken ? (
          <Box
            component="img"
            src={imageUrl}
            alt={label || "Imagen"}
            loading="lazy"
            onError={() => setPreviewBroken(true)}
            sx={{ width: "100%", height, objectFit: "cover" }}
          />
        ) : unavailableReference ? (
          <Stack
            alignItems="center"
            spacing={0.75}
            sx={{ px: 2, color: "rgba(180,34,34,.78)", textAlign: "center" }}
          >
            <BrokenImageRoundedIcon sx={{ fontSize: 38 }} />
            <Typography variant="caption" sx={{ fontWeight: 850 }}>
              La imagen guardada no está disponible
            </Typography>
          </Stack>
        ) : (
          <Stack alignItems="center" spacing={0.7} sx={{ color: "rgba(25,36,47,.46)" }}>
            <InsertPhotoRoundedIcon sx={{ fontSize: 38 }} />
            <Typography variant="caption">Sin imagen cargada</Typography>
          </Stack>
        )}
        {busy ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "rgba(255,255,255,.82)",
              display: "grid",
              placeItems: "center",
              px: 3,
            }}
          >
            <Stack spacing={1} alignItems="center" sx={{ width: "100%", maxWidth: 220 }}>
              <CircularProgress
                size={34}
                variant={Number.isFinite(progress) ? "determinate" : "indeterminate"}
                value={Number.isFinite(progress) ? progress : undefined}
                aria-label="Progreso de carga de imagen"
              />
              <Typography variant="caption" sx={{ fontWeight: 850, color: "#19242F", textAlign: "center" }}>
                {progress === 100
                  ? "Archivo enviado. Procesando imagen…"
                  : `Subiendo imagen${Number.isFinite(progress) ? `… ${progress}%` : "…"}`}
              </Typography>
              <LinearProgress
                variant={Number.isFinite(progress) ? "determinate" : "indeterminate"}
                value={Number.isFinite(progress) ? progress : undefined}
                sx={{ width: "100%" }}
                aria-label="Progreso lineal de carga de imagen"
              />
            </Stack>
          </Box>
        ) : null}
      </Box>

      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        <Button
          size="small"
          variant={unavailableReference ? "contained" : "outlined"}
          disabled={disabled || busy}
          startIcon={<CloudUploadRoundedIcon />}
          onClick={() => inputRef.current?.click()}
          sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850 }}
        >
          {hasReference ? "Reemplazar imagen" : "Elegir imagen"}
        </Button>
        {hasReference ? (
          <Button
            size="small"
            color="error"
            disabled={disabled || busy}
            startIcon={<DeleteOutlineRoundedIcon />}
            onClick={remove}
            sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850 }}
          >
            {unavailableReference ? "Quitar referencia" : "Eliminar imagen"}
          </Button>
        ) : null}
      </Stack>

      <input ref={inputRef} type="file" hidden accept={accept} onChange={upload} />
      <Typography variant="caption" sx={{ color: "rgba(25,36,47,.55)" }}>
        {hasReference
          ? fileLabelFromAsset(rawValue)
          : helperText || "Seleccioná una imagen desde tu dispositivo."}
      </Typography>
      <Typography variant="caption" sx={{ color: "rgba(25,36,47,.55)" }}>
        Formatos: {formatLabel}. Peso máximo: {formatImageSize(maxBytes)}.
      </Typography>
      {unavailableReference ? (
        <Alert severity="warning">
          La referencia se conserva para no borrar contenido por un fallo temporal. Reemplazala o quitála sólo si confirmás que ya no sirve.
        </Alert>
      ) : null}
      {error ? <Alert severity="error">{error}</Alert> : null}
    </Stack>
  );
}
